import {
  IApplicationFormData,
  IWizardCardReadiness,
  IWizardDocument,
  IWizardEmployment,
} from '../../models/application-form'
import {UPLOAD_SLOT_TYPES} from './sections/uploadSlots'

export interface ReviewDetail {
  label: string
  value: string
}

export interface MandatoryReviewItem {
  key: string
  label: string
  stepPath: string
  complete?: boolean
  pendingUpload?: boolean
  retentionType?: string
  details: ReviewDetail[]
  canNavigate?: boolean
}

export interface OptionalReviewItem {
  key: string
  label: string
  stepPath: string
  added: boolean
}

const REVIEW_ITEM_REQUIREMENT_CODES: Record<string, string[]> = {
  'the-basics': ['identity'],
  'emergency-contact': ['emergencyContact'],
  income: ['income'],
  employment: ['employment'],
  'proof-of-identity': [],
  'proof-of-employment': [],
  'proof-of-benefits': [],
  'proof-of-visa': [],
  'proof-of-student-status': [],
  'proof-of-authority': [],
  'rental-history': ['rentalHistory'],
  privacy: ['retention'],
}

const REVIEW_ITEM_DOCUMENT_TYPES: Record<string, string[]> = {
  'proof-of-identity': [UPLOAD_SLOT_TYPES.identityFront, UPLOAD_SLOT_TYPES.identityBack],
  'proof-of-employment': [UPLOAD_SLOT_TYPES.incomeDoc1, UPLOAD_SLOT_TYPES.incomeDoc2],
  'proof-of-benefits': [UPLOAD_SLOT_TYPES.incomeStatement],
  'proof-of-visa': [UPLOAD_SLOT_TYPES.visaGrant],
  'proof-of-student-status': [
    UPLOAD_SLOT_TYPES.internationalCoe,
    UPLOAD_SLOT_TYPES.enrollmentLetter,
  ],
  'proof-of-authority': [UPLOAD_SLOT_TYPES.letterOfAuthority],
  'rental-history': [UPLOAD_SLOT_TYPES.rentalLedger],
}

// These are display mappings only. Required document types and missing sections
// are evaluated once by the saved Doora Card on the server.
export const getCardRequirementStatus = (
  readiness: IWizardCardReadiness | undefined,
  codes: string[],
  documentTypes: string[] = [],
  requiresIdentificationType = false
): boolean | undefined => {
  if (!readiness) return undefined
  if (readiness.missingRequirementCodes.includes('cardCompletion')) return undefined
  if (codes.some((code) => readiness.missingRequirementCodes.includes(code))) return false
  if (documentTypes.length > 0 || requiresIdentificationType) {
    const documents = readiness.documentReadiness
    if (!documents) return undefined
    if (requiresIdentificationType && !documents.isIdentificationTypeSelected) return false
    if (documentTypes.some((type) => documents.missingTypes.includes(type))) return false
  }
  return true
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  [UPLOAD_SLOT_TYPES.identityFront]: 'Front of ID',
  [UPLOAD_SLOT_TYPES.identityBack]: 'Back of ID',
  [UPLOAD_SLOT_TYPES.incomeDoc1]: 'Income doc #1',
  [UPLOAD_SLOT_TYPES.incomeDoc2]: 'Income doc #2',
}

const RETENTION_PREFERENCE_LABELS: Record<number, string> = {
  1: 'off',
  2: '90 days',
  3: '60 days',
  60: '60 days',
  90: '90 days',
}

const isCoApplicantWizard = (data: IApplicationFormData): boolean =>
  (data.renterRole ?? '').trim().toLowerCase() === 'co-applicant'

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false
  }

  if (typeof value === 'string') {
    return value.trim().length > 0
  }

  if (typeof value === 'number') {
    return Number.isFinite(value)
  }

  if (Array.isArray(value)) {
    return value.length > 0
  }

  return true
}

const toDisplayText = (value: unknown): string => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : '-'
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '-'
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  return '-'
}

const toDisplayDate = (value: string | Date | null | undefined): string => {
  if (!value) {
    return '-'
  }

  const parsed = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return parsed.toLocaleDateString('en-AU')
}

const toDisplayFullName = (firstName?: string, lastName?: string): string => {
  const parts = [firstName, lastName]
    .map((value) => (value ?? '').trim())
    .filter((value) => value.length > 0)

  return parts.length > 0 ? parts.join(' ') : '-'
}

const getUploadedDocumentsByType = (
  documents: IWizardDocument[] | undefined,
  type: string
): IWizardDocument[] =>
  (documents ?? []).filter(
    (document) =>
      document.type === type && typeof document.filename === 'string' && document.filename.trim()
  )

const getBenefitProofLabel = (data: IApplicationFormData): string => {
  const selectedBenefitCount = [
    data.income?.hasPension === true,
    data.income?.hasStudentSupport === true,
    data.income?.isCentreLink === true,
    data.income?.hasNdis === true,
  ].filter(Boolean).length

  if (selectedBenefitCount > 1) {
    return 'Proof of government benefits'
  }

  if (data.income?.hasPension === true) {
    return 'Proof of pension'
  }

  if (data.income?.hasStudentSupport === true) {
    return 'Proof of student support'
  }

  if (data.income?.hasNdis === true) {
    return 'Proof of NDIS payments'
  }

  return 'Proof of Centrelink payments'
}

const getRetentionPreferenceLabel = (data: IApplicationFormData): string => {
  const savedLabel = data.retentionPreferenceText?.trim()
  if (savedLabel) {
    return savedLabel
  }

  const preference = Number(data.retentionPreference ?? 1)
  return RETENTION_PREFERENCE_LABELS[preference] ?? RETENTION_PREFERENCE_LABELS[1]
}

const hasEmploymentSummary = (employment?: IWizardEmployment): boolean => {
  if (!employment) {
    return false
  }

  const employmentType = (employment.employmentType ?? '').trim().toLowerCase()
  if (!employmentType) {
    return false
  }

  if (employmentType === 'self-employed' || employmentType === 'unemployed') {
    return true
  }

  return hasValue(employment.companyName)
}

export const buildMandatoryReviewItems = (data: IApplicationFormData): MandatoryReviewItem[] => {
  const documents = data.documents ?? []
  const employment = data.employment

  const identityFrontDocuments = getUploadedDocumentsByType(
    documents,
    UPLOAD_SLOT_TYPES.identityFront
  )
  const identityBackDocuments = getUploadedDocumentsByType(
    documents,
    UPLOAD_SLOT_TYPES.identityBack
  )
  const incomeStatementDocuments = getUploadedDocumentsByType(
    documents,
    UPLOAD_SLOT_TYPES.incomeStatement
  )
  const hasEmploymentIncome = data.income?.hasEmploymentIncome === true

  const firstEmploymentWithSummary = hasEmploymentSummary(employment) ? employment : undefined
  const reviewAutoDelete = getRetentionPreferenceLabel(data)
  const isCoApplicant = isCoApplicantWizard(data)

  return [
    {
      key: 'the-basics',
      label: 'Personal Details',
      stepPath: 'profile',
      complete: undefined,
      details: [
        {
          label: 'Name',
          value: toDisplayFullName(data.firstName, data.lastName),
        },
        {label: 'Email', value: toDisplayText(data.email)},
        {label: 'Date of birth', value: toDisplayDate(data.dateOfBirth)},
      ],
    },
    {
      key: 'emergency-contact',
      label: 'Emergency contact',
      stepPath: 'profile',
      complete: undefined,
      details: [
        {label: 'Name', value: toDisplayText(data.emergencyContactName)},
        {label: 'Phone', value: toDisplayText(data.emergencyContactPhone)},
        {label: 'Relationship', value: toDisplayText(data.emergencyContactRelationship)},
      ],
    },
    {
      key: 'income',
      label: 'Income',
      stepPath: 'income',
      complete: undefined,
      details: [],
    },
    {
      key: 'employment',
      label: 'Employment',
      stepPath: 'employment',
      complete: undefined,
      details: [
        {
          label: 'Employment type',
          value: toDisplayText(firstEmploymentWithSummary?.employmentType),
        },
        {label: 'Company', value: toDisplayText(firstEmploymentWithSummary?.companyName)},
      ],
    },
    {
      key: 'proof-of-identity',
      label: 'Proof of identity',
      stepPath: 'uploads',
      complete: undefined,
      details: [
        {
          label: DOCUMENT_TYPE_LABELS[UPLOAD_SLOT_TYPES.identityFront],
          value: toDisplayText(identityFrontDocuments[0]?.filename),
        },
        {
          label: DOCUMENT_TYPE_LABELS[UPLOAD_SLOT_TYPES.identityBack],
          value: toDisplayText(identityBackDocuments[0]?.filename),
        },
      ],
    },
    {
      key: 'proof-of-employment',
      label: 'Proof of employment',
      stepPath: 'uploads',
      complete: undefined,
      details: [],
    },
    {
      key: 'proof-of-benefits',
      label: getBenefitProofLabel(data),
      stepPath: 'uploads',
      complete: undefined,
      details: [
        {
          label: 'Income statement',
          value: toDisplayText(incomeStatementDocuments[0]?.filename),
        },
      ],
    },
    {
      key: 'proof-of-visa',
      label: 'Proof of visa',
      stepPath: 'uploads',
      complete: undefined,
      details: [],
    },
    {
      key: 'rental-history',
      label: 'Rental History',
      stepPath: 'rental-history',
      complete: undefined,
      details: [],
    },
    {
      key: 'privacy',
      label: 'Auto-delete',
      stepPath: 'privacy',
      complete: undefined,
      retentionType: reviewAutoDelete,
      details: [],
    },
    {
      key: 'proof-of-student-status',
      label: 'Proof of student status',
      stepPath: 'uploads',
      complete: undefined,
      details: [],
    },
    {
      key: 'proof-of-authority',
      label: 'Letter of authority',
      stepPath: 'uploads',
      complete: undefined,
      details: [],
    },
  ].filter((item) => {
    if (item.key === 'proof-of-visa') {
      return (
        data.reviewSummary?.cardReadiness?.documentReadiness?.requiredTypes.includes(
          UPLOAD_SLOT_TYPES.visaGrant
        ) === true
      )
    }

    if (item.key === 'proof-of-benefits') {
      return (
        data.reviewSummary?.cardReadiness?.documentReadiness?.requiredTypes.includes(
          UPLOAD_SLOT_TYPES.incomeStatement
        ) === true
      )
    }

    if (item.key === 'proof-of-student-status') {
      return (
        data.reviewSummary?.cardReadiness?.documentReadiness?.requiredTypes.includes(
          UPLOAD_SLOT_TYPES.internationalCoe
        ) === true
      )
    }

    if (item.key === 'proof-of-authority') {
      return (
        data.reviewSummary?.cardReadiness?.documentReadiness?.requiredTypes.includes(
          UPLOAD_SLOT_TYPES.letterOfAuthority
        ) === true
      )
    }

    if (item.key === 'employment') {
      return hasEmploymentIncome
    }

    if (item.key === 'privacy') {
      return !isCoApplicant
    }

    return true
  })
}

export const buildOptionalReviewItems = (data: IApplicationFormData): OptionalReviewItem[] => [
  {
    key: 'rental-history',
    label: 'Rental history',
    stepPath: 'rental-history',
    added: true,
  },
  {
    key: 'previous-employer',
    label: 'Previous employer',
    stepPath: 'employment',
    added: false,
  },
  {
    key: 'pets-vehicles',
    label: 'Pets & vehicles',
    stepPath: 'pets-vehicles',
    added: false,
  },
]

/**
 * The browser may group the authoritative server codes into review rows, but it
 * must not re-evaluate whether a Doora Card is complete from the draft values.
 */
export const applyCardReadinessToReviewItems = (
  items: MandatoryReviewItem[],
  readiness?: IWizardCardReadiness
): MandatoryReviewItem[] => {
  if (!readiness) {
    return items.map((item) => ({...item, complete: undefined, pendingUpload: false}))
  }

  return items.map((item) => {
    const requirementCodes = REVIEW_ITEM_REQUIREMENT_CODES[item.key]
    const documentTypes = REVIEW_ITEM_DOCUMENT_TYPES[item.key] ?? []
    const complete = requirementCodes
      ? getCardRequirementStatus(
          readiness,
          requirementCodes,
          documentTypes,
          item.key === 'proof-of-identity'
        )
      : undefined

    // Show pending only when deferred uploads are the row's remaining blockers.
    const pendingUpload =
      complete === false &&
      getCardRequirementStatus(
        readiness,
        requirementCodes ?? [],
        documentTypes.filter((type) => !readiness.documentReadiness?.deferredTypes?.includes(type)),
        item.key === 'proof-of-identity'
      ) === true

    return {...item, complete, pendingUpload}
  })
}
